import { Component } from '@theme/component';

/**
 * A swipeable image track for the item detail.
 *
 * Deliberately *not* Horizon's `slideshow-component`. That one is built for full-bleed product
 * galleries sized in `dvh`, and inside a modal it claims the whole panel and renders nothing
 * useful — and its chrome, inlined into a `<template>` on all sixty cards, cost 23 KB gzipped for
 * markup most visitors never open.
 *
 * This is CSS scroll-snap with two buttons over the top. Swipe, trackpad and keyboard all come
 * from the platform; the only JavaScript is moving by one slide and disabling the buttons at the
 * ends. No library, consistent with the theme's no-build-step rule.
 *
 * The track is a focusable scrollable region with a label, because a scrollable area that can only
 * be reached by pointer fails WCAG 2.1 — keyboard users need to be able to scroll it too.
 */

/**
 * @typedef {Object} GalleryRefs
 * @property {HTMLElement} [track]
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 */

/**
 * @extends {Component<GalleryRefs>}
 */
class CuratedGallery extends Component {
  requiredRefs = ['track'];

  /** @type {(() => void) | null} */
  #onScrollBound = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#onScrollBound = () => this.#sync();
    this.refs.track?.addEventListener('scroll', this.#onScrollBound, { passive: true });

    // Not just for resizing. This element is cloned out of a `<template>` into a dialog, so it
    // connects before it has been laid out — at which point every width is 0 and the end-detection
    // below concludes there is nothing to scroll to, disabling the next button on a four-image
    // gallery. The observer fires on first layout, which is the only reliable moment to measure.
    this.#resizeObserver = new ResizeObserver(() => this.#sync());
    if (this.refs.track) this.#resizeObserver.observe(this.refs.track);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#onScrollBound) this.refs.track?.removeEventListener('scroll', this.#onScrollBound);
    this.#onScrollBound = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** @returns {void} */
  previous() {
    this.#step(-1);
  }

  /** @returns {void} */
  next() {
    this.#step(1);
  }

  /**
   * @param {number} direction
   * @returns {void}
   */
  #step(direction) {
    const { track } = this.refs;
    if (!track) return;

    // One slide is one track width, so this stays correct whatever the panel is sized to.
    track.scrollBy({ left: track.clientWidth * direction, behavior: 'smooth' });
  }

  /** @returns {void} */
  #sync() {
    const { track, previousButton, nextButton } = this.refs;
    if (!track) return;

    // A pixel of tolerance: fractional scroll positions are normal after a smooth scroll and would
    // otherwise leave the end button enabled but inert.
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

    if (previousButton) previousButton.disabled = atStart;
    if (nextButton) nextButton.disabled = atEnd;
  }
}

if (!customElements.get('curated-gallery')) {
  customElements.define('curated-gallery', CuratedGallery);
}
